const stripe = Stripe( window.stripe_token || 'whatever' );

/**
 * Elem Class
 *
 * Wrapper class around stripes elements, to allow
 * for easier event binding, and storing state of
 * validity
 */
class Elem {

  constructor( stripeElement, component, events ) {

    Object.assign( this, {
      stripeElement,
      valid: false,
      component
    });

    this.bindEvent( 'change', this.updateValidity );
    this.bindEvents( events );
  }

  /**
   * Update validity of Elem
   *
   * @param {Object} context - { element: Elem, evt: stripe's emitted event }
   * @return {undefined}
   */
  updateValidity( context ) {
    const { complete, error } = context.evt;
    this.valid = complete && !error;
    this.error = error;
  }

  /**
   *  Bind an event handler. Intercepts the actual stripe event that is
   *    fired, and calls the handler with a new object that contains:
   *    { element: Elem, evt: original stripe event object }
   *
   *  @param {String} type  - event name
   *  @param {Function} handler - event handler
   *  @return {undefined}
   */
  bindEvent( type, handler ) {
    this.stripeElement.addEventListener( type, ( evt ) => {
      handler.call( this, { element: this, evt } );
    });
  }

  /**
   * Bind multiple event handlers to various events
   *
   * @param {Object} - key/value pairs, keys are event names,
   *   values can be function, or array of functions
   *
   * @return {undefined}
   */
  bindEvents( evtHash ) {
    const evts = Object.keys( evtHash );

    evts.forEach( type => {
      let handlers = evtHash[ type ];
      handlers = Array.isArray( handlers ) ? handlers : [ handlers ];
      handlers.forEach( handler => this.bindEvent( type, handler ) );
    });
  }

  /**
   * Get error message stored on Elem
   * @return {String} - Stripes error message
   * https://stripe.com/docs/elements/reference
   **/
  getErrorMessage() {
    return ( this.error && this.error.message ) || '';
  }

  /**
   * Call Stripe's element#mount function to render element
   * and append to the element's component
   * @return {undefined}
   **/
  render() {
    return this.stripeElement.mount( this.component );
  }

}

/**
 * ElementsModule class
 *
 * Used to create a new module that has fresh instance of
 * stripe#elements each time
 */

class ElementsModule {

  /**
   * ElementsModule constructor
   *
   * @param {Object} stripeOpts - see here for options:
   * https://stripe.com/docs/elements/reference#the-stripe-object
   *
   * @return {ElementsModule}
   */
  constructor( stripeOpts, config = {} ) {
    this.stripeElements = stripe.elements( stripeOpts );

    if ( config.paymentRequest && config.paymentRequestButton ) {
      this.setupPaymentRequest(
        config.paymentRequest,
        config.paymentRequestButton,
        config.paymentRequestStyle
      );
    }
  }

  /**
   * Set up a paymentRequest (Apple Pay, etc.)
   * @method setupPaymentRequest
   * @param  {Object}            conf – paymentRequest config
   * @return {ElementsModule}
   */

  setupPaymentRequest( conf, selector, style = {} ) {
    const req = stripe.paymentRequest( conf );

    const btn = this.stripeElements.create( 'paymentRequestButton', {
      paymentRequest: req,
      style
    });

    req.on( 'token', ev => {
      this.gotPaymentRequestToken = !!ev.token;
      this.onPaymentRequestResponse && this.onPaymentRequestResponse( ev );
      if ( ev.token ) {
        ev.complete('success');
      } else {
        ev.complete('error');
      }
    });

    req.canMakePayment().then( result => {
      if ( result ) {
        btn.mount( selector );
        this.onCanMakePayment && this.onCanMakePayment();
      } else {
        document.querySelector( selector ).style.display = 'none';
      }
    });
  }

  /*
   * return the actual stripe library
   */
  lib() {
    return stripe;
  }

  /**
   * @param {Object} conf -  config object used with stripes
   *  elements#create function. Each 'key' is the name passed to
   *  stripes elements#create, and value is an object
   *  that must contain a 'component' and optionally can contain
   *  'stripeOpts', which are options passed to that individual
   *  element creation. For reference:
   *
   *   https://stripe.com/docs/elements/reference#the-elements-object
   *
   * @param {Object} opts - {
   *
   *  defaultElementOpts: {Object} base options passed to ALL created elements,
   *    via stripes elements#create:
   *    https://stripe.com/docs/elements/reference#the-elements-object
   *
   *  events: {Object} of key (event name) and value (fn or array of fns) pairs,
   *   that will be added to each element
   * }
   */
  createElements( elConfigs, options ) {
    const { defaultElementOpts = {}, events = {} } = options;
    this.els = Object.keys( elConfigs ).reduce( ( dict, name ) => {
      const { stripeOpts = {}, component } = elConfigs[ name ];
      const stripeOptions = Object.assign( {}, defaultElementOpts, stripeOpts );
      const stripeElem = this.stripeElements.create( name, stripeOptions );
      const elem = new Elem( stripeElem, component, events );
      return Object.assign( dict, { [ name ]: elem } );
    }, {} );

  }

  /**
   * Calls Stripe lib stripe#createToken, with cardNumber.
   * @param {Object} opts - stripe options to be passed createToken method
   * @return {Promise} - resolves with result.token, or result.error
   *
   * Reference: https://stripe.com/docs/elements/reference#stripe-create-token
   */
  createToken( opts = {} ) {
    return this.lib().createToken( this.getStripeElement('cardNumber'), opts );
  }

  /**
   * Returns the raw stripe element, without the module wrapper
   * @param {String} name - the element name
   */
  getStripeElement( name ) {
    return this.els[ name ].stripeElement;
  }

  /**
   * Returns whether or not ALL fields are valid
   * @param {Boolean}
   */
  valid() {
    return this.gotPaymentRequestToken || Object.keys( this.els ).every( el => {
      return this.els[ el ].valid;
    });
  }

  /**
   * calls Stripe element #mount function for all created elements,
   * mounting them to their component passed at instantiation.
   *
   * Reference: https://stripe.com/docs/elements/reference#the-element
   */
  renderFields() {
    return Object.keys( this.els ).forEach( el => {
      this.els[ el ].render();
    });
  }

}

export default ElementsModule;
